import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE } from "../lib/config";
import contact from "../content/contact.json";

const labelCls =
  "text-[12px] tracking-[0.28em] uppercase font-semibold text-charcoal mb-3";
const inputCls =
  "bg-transparent border-b-2 border-charcoal/40 focus:border-muted_gold py-3 px-1 text-charcoal font-medium text-base outline-none transition-colors duration-500";

const ContactPage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(contact.validationMessage);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/contact`, form);
      toast.success(contact.successMessage);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : contact.errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-ivory">
      <section className="pt-40 md:pt-48 pb-32 max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto">
          <h1
            data-testid="contact-heading"
            className="font-heading font-normal text-charcoal text-5xl sm:text-6xl md:text-7xl tracking-tight"
          >
            {contact.heading}
          </h1>
          <div className="mt-8 mx-auto w-12 h-px bg-muted_gold" />
          <p className="mt-10 text-charcoal-soft text-lg leading-relaxed">
            {contact.intro}
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8"
        >
          <div className="flex flex-col">
            <label htmlFor="contact-name" className={labelCls}>
              {contact.labels.name}
            </label>
            <input
              id="contact-name"
              data-testid="contact-input-name"
              name="name"
              autoComplete="name"
              value={form.name}
              onChange={onChange}
              required
              className={inputCls}
            />
          </div>
          <div className="flex flex-col">
            <label htmlFor="contact-email" className={labelCls}>
              {contact.labels.email}
            </label>
            <input
              id="contact-email"
              data-testid="contact-input-email"
              type="email"
              name="email"
              autoComplete="email"
              value={form.email}
              onChange={onChange}
              required
              className={inputCls}
            />
          </div>
          <div className="md:col-span-2 flex flex-col">
            <label htmlFor="contact-subject" className={labelCls}>
              {contact.labels.subject}
            </label>
            <input
              id="contact-subject"
              data-testid="contact-input-subject"
              name="subject"
              value={form.subject}
              onChange={onChange}
              className={inputCls}
            />
          </div>
          <div className="md:col-span-2 flex flex-col">
            <label htmlFor="contact-message" className={labelCls}>
              {contact.labels.message}
            </label>
            <textarea
              id="contact-message"
              data-testid="contact-input-message"
              name="message"
              value={form.message}
              onChange={onChange}
              required
              rows={6}
              className={inputCls + " resize-none"}
            />
          </div>
          <div className="md:col-span-2 flex justify-center pt-6">
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={submitting}
              className="bg-charcoal text-white px-12 py-4 text-xs tracking-[0.32em] uppercase font-semibold hover:bg-muted_gold transition-colors duration-500 disabled:opacity-50"
            >
              {submitting ? contact.labels.submitting : contact.labels.submit}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
};

export default ContactPage;
